import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import BlockIcon from '@mui/icons-material/Block';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import Can from '../../components/Can';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import pedidoVentaService, { PedidoVenta } from '../../services/pedidoVentaService';
import { formatCurrency, formatClienteConNro, formatNroDocumentoVenta } from '../../utils/formatters';
import { formatFechaVencimDisplay } from '../../utils/facturaVentaFechas';
import { getApiErrorMessage } from '../../utils/apiError';
import { getPedidoEstadoChip, PEDIDO_ESTADO_FILTER_OPTIONS, PEDIDO_ESTADOS } from '../../utils/pedidoVentaEstados';
import { ROUTES } from '../../config/routes';

const comprobanteDe = (pedido: PedidoVenta): string => {
  const nro = formatNroDocumentoVenta(
    (pedido as { letra?: string }).letra,
    (pedido as { nroSuc?: number }).nroSuc,
    (pedido as { nroComprobante?: number }).nroComprobante,
  );
  return nro || String(pedido.nroFormulario || '');
};

const totalDe = (pedido: PedidoVenta): number => {
  if (pedido.importeTotal != null) return Number(pedido.importeTotal) || 0;
  return (pedido.detalles ?? []).reduce((sum, d) => {
    const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
    return sum + subtotal;
  }, 0);
};

const btnAccionSx = {
  borderRadius: 2,
  px: 1.5,
  py: 0.5,
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
} as const;

const estaAnulado = (pedido: PedidoVenta) => pedido.estado === PEDIDO_ESTADOS.ANULADO;

const esperaCerrada = (pedido: PedidoVenta) =>
  estaAnulado(pedido) || pedido.estado === PEDIDO_ESTADOS.FACTURADO;

const btnIconoSx = (bgColor: string, hoverColor: string) => ({
  bgcolor: bgColor,
  color: '#fff',
  borderRadius: 1.5,
  width: 32,
  height: 32,
  '&:hover': { bgcolor: hoverColor },
  '&:disabled': { bgcolor: '#ccc', color: '#fff' },
});

const PedidosEnEspera: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();

  const [items, setItems] = useState<PedidoVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [confirmar, setConfirmar] = useState<PedidoVenta | null>(null);
  const [anular, setAnular] = useState<PedidoVenta | null>(null);
  const [generandoId, setGenerandoId] = useState<number | null>(null);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const fetchPedidos = useCallback(async () => {
    if (!filtroListo) return;
    try {
      const response = await pedidoVentaService.getPedidosEnEspera({
        pageNumber: page + 1,
        pageSize: Math.max(1, Math.min(20, rowsPerPage)),
        searchTerm: searchTerm || undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        estado: filtroEstado || undefined,
        nroSuc: filtroNroSuc != null && filtroNroSuc > 0 ? filtroNroSuc : undefined,
      });
      setItems(response.items ?? []);
      setTotalRecords(response.totalCount ?? 0);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar los pedidos en espera'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [enqueueSnackbar, fechaDesde, fechaHasta, filtroEstado, filtroListo, filtroNroSuc, page, rowsPerPage, searchTerm]);

  useEffect(() => {
    void fetchPedidos();
  }, [fetchPedidos, reloadKey]);

  const generarPedido = async () => {
    if (!confirmar) return;
    const id = confirmar.nroFormulario;
    setGenerandoId(id);
    try {
      const pedido = await pedidoVentaService.generarPedidoDesdeEspera(id);
      const nro = comprobanteDe(pedido);
      enqueueSnackbar(
        nro ? `Se generó el pedido ${nro}` : 'Se generó el pedido',
        { variant: 'success' },
      );
      setConfirmar(null);
      setReloadKey((n) => n + 1);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el pedido'), {
        variant: 'error',
        autoHideDuration: 6000,
      });
    } finally {
      setGenerandoId(null);
    }
  };

  const anularPedido = async () => {
    if (!anular) return;
    const id = anular.nroFormulario;
    setAnulandoId(id);
    try {
      await pedidoVentaService.anularPedido(id);
      enqueueSnackbar('Pedido en espera anulado', { variant: 'success' });
      setAnular(null);
      setReloadKey((n) => n + 1);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo anular el pedido en espera'), {
        variant: 'error',
        autoHideDuration: 6000,
      });
    } finally {
      setAnulandoId(null);
    }
  };

  const ocupado = (pedido: PedidoVenta) =>
    generandoId === pedido.nroFormulario || anulandoId === pedido.nroFormulario;

  const abrirPedido = (pedido: PedidoVenta) => {
    const id = pedido.nroFormulario;
    navigate(esperaCerrada(pedido) ? ROUTES.ventas.pedidosEnEspera.ver(id) : ROUTES.ventas.pedidosEnEspera.editar(id));
  };

  const acciones = (pedido: PedidoVenta) => {
    const cerrada = esperaCerrada(pedido);
    const motivoCierre = estaAnulado(pedido)
      ? 'Pedido en espera anulado'
      : 'Ya fue facturado o pasado a pedido';
    return (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title={cerrada ? motivoCierre : 'Editar'}>
          <span>
            <IconButton
              size="small"
              disabled={cerrada}
              onClick={() => abrirPedido(pedido)}
              sx={btnIconoSx('#1976d2', '#1565c0')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={cerrada ? motivoCierre : 'Generar pedido'}>
          <span>
            <IconButton
              size="small"
              disabled={ocupado(pedido) || cerrada}
              onClick={() => setConfirmar(pedido)}
              sx={btnIconoSx('#4caf50', '#388e3c')}
            >
              {generandoId === pedido.nroFormulario
                ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                : <PlaylistAddIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Can permiso="ventas.pedidos.anular">
          <Tooltip title={estaAnulado(pedido) ? 'Pedido en espera anulado' : 'Anular'}>
            <span>
              <IconButton
                size="small"
                disabled={ocupado(pedido) || estaAnulado(pedido)}
                onClick={() => setAnular(pedido)}
                sx={btnIconoSx('#e53935', '#c62828')}
              >
                {anulandoId === pedido.nroFormulario
                  ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                  : <BlockIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Can>
      </Stack>
    );
  };

  const chipEstado = (pedido: PedidoVenta) => {
    const estado = getPedidoEstadoChip(pedido.estado);
    return (
      <Chip
        label={estado.label}
        size="small"
        sx={{ bgcolor: estado.bgColor, color: estado.textColor, fontWeight: 500, borderRadius: 1.5, minWidth: 90 }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Pedidos en espera
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unidades anotadas al registrar el pedido. No mueven stock.
              </Typography>
            </Box>
            <Can permiso="ventas.pedidos.nuevo">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SpeedIcon />}
                onClick={() => navigate(ROUTES.ventas.pedidosEnEspera.pos)}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
                }}
              >
                Nuevo
              </Button>
            </Can>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} lg={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número, comprobante, nombre o CUIT..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SearchableSelect
                  label="Estado"
                  allowEmpty
                  emptyOptionLabel="Todos"
                  value={filtroEstado || null}
                  onChange={(v) => {
                    setFiltroEstado(v ?? '');
                    setPage(0);
                  }}
                  options={PEDIDO_ESTADO_FILTER_OPTIONS}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => {
                    setFiltroNroSuc(v);
                    setPage(0);
                  }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          {isMobile ? (
            <Box>
              {items.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No hay pedidos en espera para el filtro.
                </Typography>
              )}
              {items.map((pedido) => (
                <Card key={pedido.nroFormulario} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          component="button"
                          variant="subtitle1"
                          onClick={() => abrirPedido(pedido)}
                          sx={{
                            fontWeight: 600,
                            color: '#1976d2',
                            cursor: 'pointer',
                            border: 0,
                            bgcolor: 'transparent',
                            p: 0,
                            textAlign: 'left',
                          }}
                        >
                          {comprobanteDe(pedido)}
                        </Typography>
                        {chipEstado(pedido)}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Fecha: {pedido.fechaEmision ? new Date(pedido.fechaEmision).toLocaleDateString('es-AR') : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vencimiento: {formatFechaVencimDisplay(pedido.fechaVencim ?? pedido.fechaEmision)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cliente: {formatClienteConNro(pedido.nroCliente, pedido.nombre)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        Importe Total: {formatCurrency(totalDe(pedido))}
                      </Typography>
                      <Box>{acciones(pedido)}</Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Filas por página"
              />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vencimiento</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Importe Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No hay pedidos en espera para el filtro.
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((pedido) => (
                    <TableRow key={pedido.nroFormulario} hover sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      <TableCell>
                        {pedido.fechaEmision ? new Date(pedido.fechaEmision).toLocaleDateString('es-AR') : '—'}
                      </TableCell>
                      <TableCell>{formatFechaVencimDisplay(pedido.fechaVencim ?? pedido.fechaEmision)}</TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          onClick={() => abrirPedido(pedido)}
                          sx={{ color: '#1976d2', fontWeight: 600, cursor: 'pointer' }}
                        >
                          {comprobanteDe(pedido)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatClienteConNro(pedido.nroCliente, pedido.nombre)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(totalDe(pedido))}
                      </TableCell>
                      <TableCell>{chipEstado(pedido)}</TableCell>
                      <TableCell align="center">{acciones(pedido)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Filas por página"
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmar != null} onClose={() => (generandoId == null ? setConfirmar(null) : undefined)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#1976d2' }}>Generar pedido</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se va a generar un pedido de venta con las unidades de{' '}
            {confirmar ? comprobanteDe(confirmar) : 'este pedido en espera'}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmar(null)} disabled={generandoId != null}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void generarPedido()} disabled={generandoId != null} sx={btnAccionSx}>
            {generandoId != null ? 'Generando…' : 'Generar pedido'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={anular != null} onClose={() => (anulandoId == null ? setAnular(null) : undefined)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#C62828' }}>Anular pedido en espera</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se va a anular {anular ? comprobanteDe(anular) : 'este pedido en espera'}. El pedido de venta principal no se modifica.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAnular(null)} disabled={anulandoId != null}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void anularPedido()}
            disabled={anulandoId != null}
            sx={btnAccionSx}
          >
            {anulandoId != null ? 'Anulando…' : 'Anular'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PedidosEnEspera;
